export interface Detection {
  x: number;
  y: number;
  radius: number;
}

export interface TrackedPill {
  id: string;
  x: number;
  y: number;
  radius: number;
  lastSeen: number;
  framesSeen: number;
  isStable: boolean;
}

const MATCH_THRESHOLD = 8;
const DISAPPEAR_TIMEOUT_MS = 500;
const STABLE_FRAMES = 3;
const SMOOTH = 0.3;

export class CentroidTracker {
  private tracked: Map<string, TrackedPill> = new Map();
  private nextId = 0;

  private generateId(): string {
    return `pill_${this.nextId++}`;
  }

  private dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
    return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
  }

  update(detections: Detection[]): TrackedPill[] {
    const now = Date.now();
    const usedIds = new Set<string>();

    for (const det of detections) {
      let bestId: string | null = null;
      let bestDist = MATCH_THRESHOLD;

      for (const [id, pill] of this.tracked) {
        if (usedIds.has(id)) continue;
        const d = this.dist(det, pill);
        if (d < bestDist) {
          bestDist = d;
          bestId = id;
        }
      }

      if (bestId) {
        const pill = this.tracked.get(bestId);
        if (!pill) continue;
        pill.x = pill.x * (1 - SMOOTH) + det.x * SMOOTH;
        pill.y = pill.y * (1 - SMOOTH) + det.y * SMOOTH;
        pill.radius = pill.radius * (1 - SMOOTH) + det.radius * SMOOTH;
        pill.lastSeen = now;
        pill.framesSeen += 1;
        pill.isStable = pill.framesSeen >= STABLE_FRAMES;
        usedIds.add(bestId);
      } else {
        const id = this.generateId();
        this.tracked.set(id, {
          id,
          x: det.x,
          y: det.y,
          radius: det.radius,
          lastSeen: now,
          framesSeen: 1,
          isStable: false,
        });
        usedIds.add(id);
      }
    }

    for (const [id, pill] of this.tracked) {
      if (now - pill.lastSeen > DISAPPEAR_TIMEOUT_MS) {
        this.tracked.delete(id);
      }
    }

    return Array.from(this.tracked.values());
  }

  reset() {
    this.tracked.clear();
    this.nextId = 0;
  }

  getCount(): number {
    return this.tracked.size;
  }

  getStableCount(): number {
    return Array.from(this.tracked.values()).filter((p) => p.isStable).length;
  }
}
